import { useEffect, useState, useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { IS_NATIVE, platform } from '@/platform';

/**
 * Tracks browser online/offline state via the Network Information events.
 * Used by OfflineBanner and by dashboards to show an in-app offline/retry
 * state instead of a blank view or the browser's native error page.
 *
 * In the app (M10) the answer comes from `platform.network` instead: an
 * Android WebView's `navigator.onLine` and its online/offline events do not
 * follow the device's connectivity reliably. The app feeds that one source
 * into TanStack Query's `onlineManager` (`wireNativeOnlineManager`), so the
 * banner and paused queries never disagree, and this hook reads it from there.
 */
export function useOnlineStatus(): boolean {
  return selectedHook();
}

function useBrowserOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}

const subscribeOnline = (cb: () => void) => onlineManager.subscribe(cb);
const readOnline = () => onlineManager.isOnline();

function useNativeOnlineStatus(): boolean {
  return useSyncExternalStore(subscribeOnline, readOnline, readOnline);
}

// IS_NATIVE is a build-time constant, so each build only ever calls one hook.
const selectedHook: () => boolean = IS_NATIVE ? useNativeOnlineStatus : useBrowserOnlineStatus;

let nativeWired = false;

/**
 * App only: drive `onlineManager` from the device's connectivity, once, before
 * the first query runs. Unknown counts as online (the native layer's rule), so
 * nothing pauses until the device actually says it is offline. A no-op on the
 * website, where TanStack's own browser listener stays in place.
 */
export function wireNativeOnlineManager(): void {
  if (!IS_NATIVE || nativeWired) return;
  nativeWired = true;
  onlineManager.setEventListener((setOnline) => {
    // A change event is newer than the first read, which may answer after
    // it: once one has arrived, the read is ignored (Codex on #1133).
    let changed = false;
    const unsubscribe = platform.network.onChange((online) => {
      changed = true;
      setOnline(online);
    });
    void platform.network.isOnline().then(
      (online) => {
        if (!changed) setOnline(online);
      },
      () => {
        if (!changed) setOnline(true);
      },
    );
    return unsubscribe;
  });
}
